import { XMLParser, XMLValidator } from 'fast-xml-parser';

import { ReminderResponse } from '@core';
import { TranslationKeys } from '@ui-core/types';
import {
  Date,
  Medication,
  MedicationAdministrationTypes,
  MedicationDosageTimes,
  MedicationPhaseTypes,
  MedicationPlan,
  Site,
} from '@type';

import {
  convertHexToChar,
  convertToDate,
  getDateHour,
  getDateUTCOffset,
  isDateToday,
  timestampToDate,
} from '..';
import {
  MEDICATION_DOSAGE_HOURS,
  SPECIFIC_MEDICATION_TIMINGS,
} from './medication.constant';

export const getMedicationDosageTime = (date: Date) => {
  const hours = getDateHour(date);
  if (hours < MEDICATION_DOSAGE_HOURS[MedicationDosageTimes.Morning])
    return MedicationDosageTimes.Other;
  if (hours < MEDICATION_DOSAGE_HOURS[MedicationDosageTimes.Midday])
    return MedicationDosageTimes.Morning;
  if (hours < MEDICATION_DOSAGE_HOURS[MedicationDosageTimes.Evening])
    return MedicationDosageTimes.Midday;
  if (hours < MEDICATION_DOSAGE_HOURS[MedicationDosageTimes.Night])
    return MedicationDosageTimes.Evening;
  return MedicationDosageTimes.Night;
};

export const getMedicationAdministrationType = (
  administrationType: MedicationAdministrationTypes
): TranslationKeys =>
  ({
    [MedicationAdministrationTypes.Intravenous]:
      'medication.administration.intravenous' as TranslationKeys,
    [MedicationAdministrationTypes.Oral]:
      'medication.administration.oral' as TranslationKeys,
    [MedicationAdministrationTypes.Subcutaneous]:
      'medication.administration.subcutaneous' as TranslationKeys,
    [MedicationAdministrationTypes.Narcotic]:
      'medication.administration.narcotic' as TranslationKeys,
  })[administrationType];

const extractDosageFromOccurrence = (
  date: Date,
  dosageDescriptions?: string,
  isMedicationPlan = false
) => {
  if (!dosageDescriptions) return;
  const dosageObject = JSON.parse(dosageDescriptions) as {
    morningDosage?: string;
    middayDosage?: string;
    eveningDosage?: string;
    nightDosage?: string;
    specificTimeDosage?: string;
  };
  if (!SPECIFIC_MEDICATION_TIMINGS.includes(date.hour()) && !isMedicationPlan)
    return dosageObject.specificTimeDosage;
  const dosage = {
    [MedicationDosageTimes.Morning]: dosageObject?.morningDosage,
    [MedicationDosageTimes.Midday]: dosageObject?.middayDosage,
    [MedicationDosageTimes.Evening]: dosageObject?.eveningDosage,
    [MedicationDosageTimes.Night]: dosageObject?.nightDosage,
    [MedicationDosageTimes.Other]: dosageObject?.specificTimeDosage,
  };
  return dosage[getMedicationDosageTime(date)];
};

export const getMedicationPhaseFromId = (
  phaseId: MedicationPhaseTypes
): TranslationKeys =>
  ({
    [MedicationPhaseTypes.Planning]: 'medication.phase.planning',
    [MedicationPhaseTypes.Treatment]: 'medication.phase.treatment',
    [MedicationPhaseTypes.FollowUp]: 'medication.phase.followUp',
    [MedicationPhaseTypes.Diagnosis]: 'medication.phase.diagnosis',
    [MedicationPhaseTypes.Screening]: 'medication.phase.screening',
  })[phaseId] as TranslationKeys;

export const extractMedicationsFromReminder = (
  reminder: ReminderResponse,
  site: Site
): Medication[] => {
  const medicationsList = [] as Medication[];
  for (const reminderOccurrence of reminder.allOccurrences) {
    const occurrenceDate = timestampToDate(
      reminderOccurrence.unixEpochs - getDateUTCOffset() * 60
    );
    if (!isDateToday(occurrenceDate)) continue;

    const isMedicationPlan = reminder.medication.medicationPlanId !== null;
    medicationsList.push({
      title: reminder.medication.name,
      id: reminder.medication.id,
      reminderId: reminder.id,
      icd: reminder.medication.icd,
      administrationType: reminder.medication.medicationAdministrationTypeId,
      sober: reminder.medication.sober,
      notes: reminder.medication.notes,
      uuid: reminder.medication.uuid,
      maximumDosage: reminder.medication.maximumDosage,
      phase: getMedicationPhaseFromId(reminder.medication.phaseTypeId),
      form: reminder.medication.form,
      patientId: reminder.patientId,
      medicationPlanId: reminder.medication.medicationPlanId,
      isMedicationPlan,
      site,
      isAnswered: !!reminderOccurrence.response?.status,
      lifespan: reminderOccurrence.lifespan,
      occurrence: {
        id: reminderOccurrence.id,
        createdAt: convertToDate(reminderOccurrence.createdAt),
        date: occurrenceDate,
        dosage: extractDosageFromOccurrence(
          occurrenceDate,
          reminderOccurrence.additionalAttribute.attributesAddon,
          isMedicationPlan
        ),
      },
    });
  }
  return medicationsList;
};

export const extractMedicationPlanData = (
  medicationPlan: string
): MedicationPlan => {
  if (!XMLValidator.validate(medicationPlan))
    throw new Error('Invalid medication plan');
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '',
  });
  const JSONdata = parser.parse(medicationPlan) as {
    MP: {
      v: string;
      U: string;
      P: {
        b: string;
        g: string;
        f: string;
      };
      z: string;
      a: string;
    };
  };

  const version = +JSONdata.MP.v;
  const id = JSONdata.MP.U;
  const compactBirthDate = JSONdata.MP.P.b;

  const pages = {
    page: +JSONdata.MP?.a || 1,
    totalPages: +JSONdata.MP?.z || 1,
  };

  const name = convertHexToChar(`${JSONdata.MP?.P.g} ${JSONdata.MP?.P.f}`);
  let birthDate = convertToDate(compactBirthDate);
  if (Number.isSafeInteger(+compactBirthDate)) {
    const year = compactBirthDate.slice(0, 4);
    const month = compactBirthDate.slice(4, 6);
    const day = compactBirthDate.slice(6);
    birthDate = convertToDate(`${year}-${month}-${day}`);
  }

  return {
    id,
    version,
    name,
    birthDate,
    ...pages,
    xmlData: medicationPlan,
  };
};

export const isMedicationPlan = (medications: Medication[]) =>
  medications.some(({ medicationPlanId }) => medicationPlanId !== null);
